import { useState } from "react"
import BottomSheet from "../../../components/BottomSheet/BottomSheet"
import CloseButton from "../../../components/CloseButton/CloseButton"
import PrimaryButton from "../../../components/PrimaryButton/PrimaryButton"
import LifetimeDisclaimerPopup from "../LifetimeDisclaimerPopup/LifetimeDisclaimerPopup"
import { useAppState } from "../../../context/appStateContext"
import { usePurchase } from "../../../context/purchaseContext"
import useStrings from "../../../localization/useStrings"
import { formatCurrency } from "../../../utils/utils"
import Svg from "../../../utils/svg"
import styles from "./LifetimeBottomSheet.module.scss"
import native from "../../../native/native"

interface Props {
  visible: boolean
  onClose: () => void
  onCtaClick: () => void
}

const LifetimeBottomSheet = ({ visible, onClose, onCtaClick }: Props) => {
  const strings = useStrings().bubbleExpiration
  const { state: appState } = useAppState()
  const { state: purchaseState } = usePurchase()
  const [showDisclaimerPopup, setShowDisclaimerPopup] = useState(false)
  const bottomInset = appState.initialData?.originalBottomInset || 0

  const yearlyProduct = purchaseState.selectedPlan?.yearlyProduct
  const lifetimePrice = yearlyProduct
    ? formatCurrency(
        Math.round(yearlyProduct.price * 5 + 0.01) - 0.01,
        yearlyProduct.priceLocale,
      )
    : null

  const features = [
    strings?.lifetimeFeature1,
    strings?.lifetimeFeature2,
    strings?.lifetimeFeature3,
  ].filter(Boolean) as string[]

  return (
    <>
      <BottomSheet
        visible={visible}
        onClickOutside={onClose}
        containerClassName={styles.sheetContainer}
      >
        <div
          className={styles.wrapper}
          style={
            {
              "--bottom-inset": `${bottomInset}px`,
            } as React.CSSProperties
          }
        >
          <div className={styles.container}>
            <CloseButton className={styles.closeButton} onClick={onClose} />
            <div className={styles.iconWrapper}>
              <Svg.PremiumIcon className={styles.icon} />
            </div>
            <label className={styles.title}>{strings?.lifetimeTitle}</label>
            {lifetimePrice && (
              <label className={styles.price}>{lifetimePrice}</label>
            )}
            <div className={styles.featureList}>
              <div className={styles.featureListContent}>
                {features.map((feature) => (
                  <div key={feature} className={styles.featureItem}>
                    <Svg.CheckmarkCircle className={styles.checkIcon} />
                    <span className={styles.featureText}>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div
              className={styles.infoBox}
              onClick={() => {
                native.logMixpanelEvent("html_lifetime_disclaimer")
                setShowDisclaimerPopup(true)
              }}
            >
              <Svg.Info className={styles.infoIcon} />
              <span className={styles.infoText}>
                {strings.lifetimeSheetDisclaimer}
              </span>
            </div>
            <PrimaryButton className={styles.cta} onClick={onCtaClick}>
              {strings?.lifetimeCta ?? strings?.lifetime}
            </PrimaryButton>
          </div>
        </div>
      </BottomSheet>
      <LifetimeDisclaimerPopup
        visible={showDisclaimerPopup}
        onClose={() => setShowDisclaimerPopup(false)}
      />
    </>
  )
}

export default LifetimeBottomSheet
